import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import clientService from '../services/clientService';

const BillingInfoSection = ({ clientId, billingInfoList = [], onChange, readOnly = false, errors = {} }) => {
  const [billingInfoItems, setBillingInfoItems] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const prevLengthRef = useRef(0);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const currentLength = billingInfoList?.length || 0;
    const prevLength = prevLengthRef.current;
    
    if (billingInfoList && billingInfoList.length > 0) {
      setBillingInfoItems(billingInfoList);
      // Only reset expandedIndex if the array length changed (item added/removed) or on initial mount
      if (isInitialMount.current || currentLength !== prevLength) {
        if (currentLength > 0) {
          // If item was added, expand the new one; otherwise keep current or default to 0
          setExpandedIndex(prevExpanded => {
            if (prevLength < currentLength) {
              // Item added - expand the new one
              return currentLength - 1;
            } else if (prevLength > currentLength) {
              // Item removed - keep expanded if still valid, otherwise expand last
              return prevExpanded !== null && prevExpanded < currentLength ? prevExpanded : Math.max(0, currentLength - 1);
            } else {
              // Same length but first mount
              return prevExpanded !== null ? prevExpanded : 0;
            }
          });
        }
        prevLengthRef.current = currentLength;
        isInitialMount.current = false;
      }
    } else {
      // Initialize with empty item if no data (only on initial mount)
      if (isInitialMount.current) {
        setBillingInfoItems([{
          label: '',
          vat_number: '',
          tax_office: '',
          business_name: '',
          profession: '',
          address: '',
          postal_code: '',
          city: '',
          country: 'Greece',
          is_default: false,
          is_active: true
        }]);
        setExpandedIndex(0);
        prevLengthRef.current = 1;
        isInitialMount.current = false;
      }
    }
  }, [billingInfoList]);

  // Auto-expand accordion with errors
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      // Find first accordion with error
      for (let i = 0; i < billingInfoItems.length; i++) {
        const hasError = Object.keys(errors).some(key => key.startsWith(`billing_info.${i}.`));
        if (hasError) {
          setExpandedIndex(i);
          break;
        }
      }
    }
  }, [errors, billingInfoItems.length]);

  const handleAddNew = () => {
    const newItem = {
      label: '',
      vat_number: '',
      tax_office: '',
      business_name: '',
      profession: '',
      address: '',
      postal_code: '',
      city: '',
      country: 'Greece',
      is_default: false,
      is_active: true
    };
    const updated = [...billingInfoItems, newItem];
    setBillingInfoItems(updated);
    setExpandedIndex(updated.length - 1);
    onChange(updated);
  };

  const handleRemove = (index) => {
    if (billingInfoItems.length <= 1) {
      // Keep at least one item
      return;
    }
    const updated = billingInfoItems.filter((_, i) => i !== index);
    setBillingInfoItems(updated);
    if (onChange) onChange(updated);
  };

  const handleFieldChange = (index, field, value) => {
    const updated = [...billingInfoItems];
    updated[index] = { ...updated[index], [field]: value };
    
    // If setting as default, unset others
    if (field === 'is_default' && value === true) {
      updated.forEach((item, i) => {
        if (i !== index) {
          item.is_default = false;
        }
      });
    }
    
    setBillingInfoItems(updated);
    if (onChange) onChange(updated);
  };

  const handleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon /> Επιχειρηματικά / Φορολογικά Στοιχεία
        </Typography>
        {!readOnly && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Προσθήκη Σετ
          </Button>
        )}
      </Box>

      {billingInfoItems.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          Δεν υπάρχουν φορολογικά στοιχεία. Κάντε κλικ στο "Προσθήκη Σετ" για να προσθέσετε.
        </Typography>
      ) : (
        billingInfoItems.map((item, index) => (
          <Accordion
            key={index}
            expanded={expandedIndex === index}
            onChange={() => handleExpand(index)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  {item.is_default && (
                    <Chip label="Προεπιλογή" size="small" color="primary" />
                  )}
                  <Typography variant="subtitle2">
                    {item.label || `Σετ ${index + 1}`}
                    {item.business_name && ` - ${item.business_name}`}
                    {item.vat_number && ` (ΑΦΜ: ${item.vat_number})`}
                  </Typography>
                </Box>
                {!readOnly && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(index);
                    }}
                    disabled={billingInfoItems.length <= 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ετικέτα (π.χ. Κύρια, Εταιρεία 2)"
                    value={item.label || ''}
                    onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                    size="small"
                    disabled={readOnly}
                    error={!!errors[`billing_info.${index}.label`]}
                    helperText={errors[`billing_info.${index}.label`] || "Ονομασία για αναγνώριση"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item.is_default || false}
                        onChange={(e) => handleFieldChange(index, 'is_default', e.target.checked)}
                        disabled={readOnly}
                      />
                    }
                    label="Προεπιλεγμένο"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Επωνυμία"
                    value={item.business_name || ''}
                    onChange={(e) => handleFieldChange(index, 'business_name', e.target.value)}
                    size="small"
                    disabled={readOnly}
                    error={!!errors[`billing_info.${index}.business_name`]}
                    helperText={errors[`billing_info.${index}.business_name`]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Επάγγελμα"
                    value={item.profession || ''}
                    onChange={(e) => handleFieldChange(index, 'profession', e.target.value)}
                    size="small"
                    disabled={readOnly}
                    error={!!errors[`billing_info.${index}.profession`]}
                    helperText={errors[`billing_info.${index}.profession`]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ΑΦΜ"
                    value={item.vat_number || ''}
                    onChange={(e) => handleFieldChange(index, 'vat_number', e.target.value)}
                    size="small"
                    disabled={readOnly}
                    error={!!errors[`billing_info.${index}.vat_number`]}
                    helperText={errors[`billing_info.${index}.vat_number`]}
                    inputProps={{ maxLength: 20 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ΔΟΥ"
                    value={item.tax_office || ''}
                    onChange={(e) => handleFieldChange(index, 'tax_office', e.target.value)}
                    size="small"
                    disabled={readOnly}
                    error={!!errors[`billing_info.${index}.tax_office`]}
                    helperText={errors[`billing_info.${index}.tax_office`]}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Διεύθυνση"
                    value={item.address || ''}
                    onChange={(e) => handleFieldChange(index, 'address', e.target.value)}
                    size="small"
                    multiline
                    rows={2}
                    disabled={readOnly}
                    error={!!errors[`billing_info.${index}.address`]}
                    helperText={errors[`billing_info.${index}.address`]}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Ταχυδρομικός Κώδικας"
                    value={item.postal_code || ''}
                    onChange={(e) => handleFieldChange(index, 'postal_code', e.target.value)}
                    size="small"
                    disabled={readOnly}
                    error={!!errors[`billing_info.${index}.postal_code`]}
                    helperText={errors[`billing_info.${index}.postal_code`]}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Πόλη"
                    value={item.city || ''}
                    onChange={(e) => handleFieldChange(index, 'city', e.target.value)}
                    size="small"
                    disabled={readOnly}
                    error={!!errors[`billing_info.${index}.city`]}
                    helperText={errors[`billing_info.${index}.city`]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Χώρα"
                    value={item.country || 'Greece'}
                    onChange={(e) => handleFieldChange(index, 'country', e.target.value)}
                    size="small"
                    disabled={readOnly}
                    error={!!errors[`billing_info.${index}.country`]}
                    helperText={errors[`billing_info.${index}.country`]}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default BillingInfoSection;

